import { DialogTitle, Divider, Grid, GridProps, useTheme } from '@mui/material';
import { Condition, ContextForm, DatePicker, IConditionProps, IFormAfterSubmit, IFormLookupFilterProps, InputOnlyField, IValidationError, Select, Text, useFormById, useFormMetadata } from '@ngt/forms';
import { ConditionMode, FormManager, ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Contract, ContractStatuses, FormDefinitions, Lot, LotSaleStatuses, Stage, StageSalesStatuses, StageStatuses } from '../api/dtos';
import ContractSummariesContext from '../contexts/ContractSummariesContext';
import LotDetailsContext from '../contexts/LotDetailsContext';
import { DollarInput, FormSection, IFormDialogProps, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent, StyledDivider, StyledFormColumn, StyledFormSubmitButton } from './inputs/CommonFormElements';

const COLUMN_SIZING: GridProps['size'] = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
    xl: 6
} 

enum DateType  {
    SalesAdvice = 1,
    IssueDate = 2,
    ExchangeDate = 3,
    SettleDate = 4
}


const ContractForm: FC<IFormDialogProps> = (props) => {
    const theme = useTheme();
    const { refresh: refreshContractSummaries } = useContext(ContractSummariesContext);

    const lotMetadata = useFormMetadata(FormDefinitions.Lot);
    const stageMetadata = useFormMetadata(FormDefinitions.Stage);


    const params = useParams();
    const lotId = Number(params.lotId);
    const stageId = Number(params.stageId);

    const {data: lot} = useFormById<Lot>(lotMetadata, lotId);
    const {data: stage} = useFormById<Stage>(stageMetadata, stageId);

    const {refresh: refreshLot} = useContext(LotDetailsContext);

    const formRef = useRef<FormManager<Contract, IValidationError>>(null);

    const salesAdviceDateEnabledConditionFn: IConditionProps<Contract>['condition'] = useCallback(() => {
        return (
            lot?.saleStatus === LotSaleStatuses.Available &&
            !lot?.displayHome
        );
    }, [lot]);
    
    const issueDateEnabledConditionFn: IConditionProps<Contract>['condition'] = useCallback(({ formState }) => {
        return (
            !!formState?.value?.salesAdviceDate
        );
    }, []);
    
    const exchangeDateEnabledConditionFn: IConditionProps<Contract>['condition'] = useCallback(({ formState }) => {
        return (
            !!formState?.value?.issueDate
        );
    }, []);

    const settleDateEnabledConditionFn: IConditionProps<Contract>['condition'] = useCallback(({ formState }) => {
        return (
            !!formState?.value?.exchangeDate &&
            stage?.status == StageStatuses.Registered
        );
    }, [stage]);
    
    const manualExpectedSettlementDateEnabledConditionFn: IConditionProps<Contract>['condition'] = useCallback(({ formState }) => {
        return (
            formState?.value?.status !== ContractStatuses.ContractSettled &&
            formState?.value?.status !== ContractStatuses.ContractCancelled
        );
    }, [lot]);

    const contractStatusFilter: IFormLookupFilterProps<Contract>['filterFunction'] = useCallback((formState, _propertyPath, lookup) => {
        if (!lookup) {
            return undefined;
        }
    
        return {
            ...lookup,
            items: lookup.items
                ?.map(x => {
                    const disabled = !(
                        (
                            x.id == ContractStatuses.ContractCancelled &&
                            formState.value?.status !== ContractStatuses.ContractSettled
                        ) || (
                            x.id == ContractStatuses.SalesAdvice &&
                            (
                                stage?.salesStatus === StageSalesStatuses.Archived ||
                                stage?.salesStatus === StageSalesStatuses.Current 
                            ) && 
                            formState.value?.salesAdviceDate //&&
                            // !formState.value?.issueDate &&
                            // !formState.value?.exchangeDate &&
                            // !formState.value?.settleDate
                        ) || (
                            x.id == ContractStatuses.ContractIssued &&
                            (
                                stage?.salesStatus === StageSalesStatuses.Archived ||
                                stage?.salesStatus === StageSalesStatuses.Current 
                            ) && 
                            formState.value?.issueDate //&&
                            // !formState.value?.exchangeDate &&
                            // !formState.value?.settleDate
                        ) || (
                            x.id == ContractStatuses.ContractExchanged &&
                            (
                                stage?.salesStatus === StageSalesStatuses.Archived ||
                                stage?.salesStatus === StageSalesStatuses.Current 
                            ) && 
                            formState.value?.exchangeDate //&&
                            // !formState.value?.settleDate
                        ) || (
                            x.id == ContractStatuses.ContractSettled &&
                            (
                                stage?.salesStatus === StageSalesStatuses.Archived ||
                                stage?.salesStatus === StageSalesStatuses.Current 
                            ) && 
                            formState.value?.settleDate
                        )
                    );

                    return {...x, disabled: !!disabled}
                }
            )
        }
    }, [stage]);

    const handleDateChange = (dateType: DateType) => {
        if(dateType == DateType.SalesAdvice && formRef.current?.getFieldValue('status') == undefined) {
            formRef.current?.setFieldValue('status', ContractStatuses.SalesAdvice, true, true, true, true);
        }
        else if(
            dateType == DateType.IssueDate && 
            (
                formRef.current?.getFieldValue('status') == undefined ||
                (formRef.current?.getFieldValue('status') as number) <= (ContractStatuses.ContractIssued as number)
            )
        ) {
            formRef.current?.setFieldValue('status', ContractStatuses.ContractIssued, true, true, true, true);
        }
        else if(
            dateType == DateType.ExchangeDate && 
            (
                formRef.current?.getFieldValue('status') == undefined ||
                (formRef.current?.getFieldValue('status') as number) <= (ContractStatuses.ContractExchanged as number)
            )
        ) {
            formRef.current?.setFieldValue('status', ContractStatuses.ContractExchanged, true, true, true, true);
        }
        else if(
            dateType == DateType.SettleDate && 
            (
                formRef.current?.getFieldValue('status') == undefined ||
                (formRef.current?.getFieldValue('status') as number) <= (ContractStatuses.ContractSettled as number)
            )
        ) {
            formRef.current?.setFieldValue('status', ContractStatuses.ContractSettled, true, true, true, true);
        }
    }

    const onAfterSubmit: IFormAfterSubmit<Contract> = useCallback(async (_newValue, _formState, _formActions) => {
        await refreshContractSummaries();
        await refreshLot();
        props.onClose();
    }, [refreshContractSummaries, props.onClose]);

    return (
        <ContextForm
            onAfterSubmit={onAfterSubmit}
            validateOn={ValidateOn.onChange}
            validateOnReset
            formRef={formRef}
        >
            <StyledDialog {...props}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    {props.edit ? "Edit" : "Add"} Contract
                </DialogTitle>
                <StyledDialogContent>
                    <FormSection>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Status"
                                component={Select}
                                propertyPath={'status'}
                                LookupFilterProps={{
                                    filterFunction: contractStatusFilter,
                                    subscription: {
                                        value: true
                                    }
                                }}
                            />
                            <DollarInput
                                label="List Price" 
                                propertyPath={'listPrice'}
                                disabled
                            />
                            <DollarInput
                                label="Price" 
                                propertyPath={'price'}
                            />
                            <DollarInput
                                label="Approved Rebate"
                                propertyPath={'approvedRebate'}
                                disabled
                            />
                            <DollarInput
                                label="Rebate" 
                                propertyPath={'rebate'}
                            />
                            
                            <Condition 
                                condition={salesAdviceDateEnabledConditionFn}
                                mode={ConditionMode.Enable}
                                subscription={{value: true}}
                            >
                                <InputOnlyField 
                                    label="Sales Advice Date"
                                    onChange={() => handleDateChange(DateType.SalesAdvice)}
                                    component={DatePicker}
                                    propertyPath={'salesAdviceDate'}
                                />
                            </Condition>
                            
                            <Condition 
                                condition={issueDateEnabledConditionFn}
                                mode={ConditionMode.Enable}
                                subscription={{value: true}}
                            >
                                <InputOnlyField
                                    label="Issue Date" 
                                    onChange={() => handleDateChange(DateType.IssueDate)}
                                    component={DatePicker}
                                    propertyPath={'issueDate'}
                                />
                            </Condition>
                            <Condition 
                                condition={exchangeDateEnabledConditionFn}
                                mode={ConditionMode.Enable}
                                subscription={{value: true}}
                            >
                                <InputOnlyField
                                    label="Exchange Date" 
                                    onChange={() => handleDateChange(DateType.ExchangeDate)}
                                    component={DatePicker}
                                    propertyPath={'exchangeDate'}                    
                                />
                            </Condition>
                            <InputOnlyField
                                label="Expected Settlement Date" 
                                component={DatePicker}
                                propertyPath={'calculatedExpectedSettleDate'}
                                disabled           
                            />
                            <Condition 
                                condition={manualExpectedSettlementDateEnabledConditionFn}
                                mode={ConditionMode.Enable}
                                subscription={{value: true}}
                            >
                                <InputOnlyField
                                    label="Manual Expected Settlement Date" 
                                    component={DatePicker}
                                    propertyPath={'expectedSettleDate'}                 
                                />
                            </Condition>
                            <Condition 
                                condition={settleDateEnabledConditionFn}
                                mode={ConditionMode.Enable}
                                subscription={{value: true}}
                            >
                                <InputOnlyField
                                    label="Actual Settlement Date" 
                                    onChange={() => handleDateChange(DateType.SettleDate)}
                                    component={DatePicker}
                                    propertyPath={'settleDate'}                 
                                />
                            </Condition>
                        </StyledFormColumn>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Agent Location"
                                component={Select}
                                propertyPath={'agentLocationId'}
                                MenuProps={{
                                    sx: {
                                        maxHeight: 500
                                    }
                                }}
                            />
                            <DollarInput 
                                label="Council Rate Adjust (ex. GST)"
                                propertyPath={'rateAdjust'}
                            />
                            <DollarInput
                                label="Water Rate Adjust (ex. GST)"
                                propertyPath={'waterAdjust'}
                            />
                            <DollarInput 
                                label="Legals (ex. GST)"
                                propertyPath={'legals'}
                            />
                            <DollarInput 
                                label="Commission (calculated) (ex. GST)"
                                propertyPath={'calculatedCommission'}
                                disabled
                            />
                            <DollarInput
                                label="Commission (actual) (ex. GST)"
                                propertyPath="commission"
                            />
                            <DollarInput
                                label="Interest"
                                propertyPath={'halfInterest'}
                            />
                            <DollarInput 
                                label="Deposit"
                                propertyPath={'deposit'}
                            />
                            <InputOnlyField
                                label="Date Back on Market"
                                component={DatePicker}
                                propertyPath={'dateBackOnMarket'}
                            />
                            {/* <FixedHeightInputOnlyField
                                label="No Interest"
                                component={Checkbox}
                                propertyPath={'noInterest'}
                            /> */}
                        </StyledFormColumn>
                    </FormSection>
                    <StyledDivider />
                    <FormSection>
                        <Grid sx={{
                            width: "100%"
                        }}>
                            <InputOnlyField
                                label="Manual Expected Settlement Date Notes"
                                component={Text}
                                multiline
                                rows={5}
                                propertyPath={'manualExpectedSettleDateNotes'}
                            />
                        </Grid>
                    </FormSection>
                </StyledDialogContent>
                <Divider />
                <StyledDialogActions>
                    <StyledFormSubmitButton variant="contained">Submit</StyledFormSubmitButton>
                    <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
        </ContextForm>
    );
}

export default ContractForm;